import React, { useEffect, useRef, useCallback } from 'react';
import { GOOGLE_WEB_CLIENT_ID } from '../../firebase';
import { useAuth } from '../../contexts/FirebaseAuthContext';

// Loads the Google Identity Services script once and resolves when ready.
let gisScriptPromise = null;
const loadGis = () => {
  if (gisScriptPromise) return gisScriptPromise;
  gisScriptPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const existing = document.getElementById('google-gsi-client');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Google sign-in')));
      return;
    }
    const s = document.createElement('script');
    s.id = 'google-gsi-client';
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Google sign-in'));
    document.head.appendChild(s);
  });
  return gisScriptPromise;
};

/**
 * Renders Google's official "Continue with Google" button via Google Identity
 * Services. On click, Google returns an ID token directly to our page (no
 * cross-site redirect handler), which we hand to Firebase. This is the
 * reliable Google sign-in path on iOS Safari.
 */
const GoogleSignInButton = ({ onSuccess, onError, disabled }) => {
  const { loginWithGoogleIdToken } = useAuth();
  const containerRef = useRef(null);

  const handleCredential = useCallback(
    async (response) => {
      if (!response?.credential) {
        onError?.('No credential returned from Google. Please try again.');
        return;
      }
      const result = await loginWithGoogleIdToken(response.credential);
      if (result?.success) onSuccess?.();
      else onError?.(result?.error || 'Google sign-in failed');
    },
    [loginWithGoogleIdToken, onSuccess, onError]
  );

  useEffect(() => {
    let cancelled = false;
    loadGis()
      .then(() => {
        if (cancelled || !window.google?.accounts?.id || !containerRef.current) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_WEB_CLIENT_ID,
          callback: handleCredential,
          auto_select: false,
          itp_support: true,
        });
        const width = Math.max(200, Math.min(400, containerRef.current.offsetWidth || 320));
        containerRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(containerRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'center',
          width,
        });
      })
      .catch((err) => {
        console.error('GIS load error:', err);
        onError?.('Could not load Google sign-in. Please use email and password.');
      });
    return () => {
      cancelled = true;
    };
  }, [handleCredential, onError]);

  return (
    <div
      style={{
        marginBottom: '12px',
        display: 'flex',
        justifyContent: 'center',
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      <div ref={containerRef} style={{ width: '100%', display: 'flex', justifyContent: 'center' }} />
    </div>
  );
};

export default GoogleSignInButton;
